import { type MVF, type MVFProfile } from "@lib/tranche";
import { z } from "zod";

// ODSR = Outside Dollar Substitution Rate. We consider how much "outside"
// dollars should substitute for S-Process dollars:
//
//   dollars = sProcessDollars + ODSR * outsideDollars
//           = sProcessDollars + ODSR * sProcessDollars / matchRate
//
// Where dollars is the number of dollars we want them to receive and
// sProcessDollars is dollars actually allocated by the S-Process.
export const MATCHING_ODSR = 0.66;

export const matchingMvfScalesSchema = z.object({
  horizontalScale: z.number(),
  verticalScale: z.number(),
});

export type MatchingMvfScales = z.infer<typeof matchingMvfScalesSchema>;
export type MatchingMvfScalesProfile = {
  [evaluableId: string]: MatchingMvfScales;
};

export function scaleMvf(
  mvf: MVF,
  { horizontalScale, verticalScale }: MatchingMvfScales,
): MVF {
  return mvf.map((point) => ({
    ...point,
    dollar: point.dollar * horizontalScale,
    value: point.value * verticalScale,
  }));
}

export function scaleMvfProfile(
  mvfProfile: MVFProfile,
  scalesProfile: MatchingMvfScalesProfile,
  matchRates: { [evaluableId: string]: number | undefined },
): MVFProfile {
  const matchingMvfProfile: MVFProfile = {};
  for (const [evaluableId, standardMvf] of Object.entries(mvfProfile)) {
    const explicitScales = scalesProfile[evaluableId];
    const matchRate = matchRates[evaluableId];
    if (explicitScales) {
      matchingMvfProfile[evaluableId] = scaleMvf(standardMvf, explicitScales);
      continue;
    }

    if (matchRate != null && matchRate > 0) {
      matchingMvfProfile[evaluableId] = scaleMvf(
        standardMvf,
        getDefaultMatchingMvfScales(matchRate),
      );
    }
  }
  return matchingMvfProfile;
}

export const DEFAULT_MATCHING_MVF_SCALES: MatchingMvfScales = {
  horizontalScale: 1,
  verticalScale: 1,
};

export function getDefaultMatchingMvfScales(
  matchRate: number | undefined,
): MatchingMvfScales {
  if (matchRate == null || matchRate <= 0) {
    return DEFAULT_MATCHING_MVF_SCALES;
  }

  // We solve the equation x = y + MATCHING_ODSR * y / matchRate (where x is the
  // amount the algorithm determines they should receive and y is the amount we
  // actually grant them) for y, then y = x * horizontalScale (see comment on
  // MATCHING_ODSR).
  const horizontalScale = 1 / (1 + MATCHING_ODSR / matchRate);

  // Stretch the y axis since each dollar from the S-Process turns into 1 + 1 /
  // matchRate dollars
  const verticalScale = 1 + 1 / matchRate;
  return { horizontalScale, verticalScale };
}
